import { getAuth } from 'firebase/auth';
import { PRIVACY_POLICY_VERSION } from '@/utils/privacyContent';

const PROFILE_KEY = 'coolliving_scraped_lead_profile';
const SENT_LISTINGS_KEY = 'coolliving_scraped_lead_sent_listings';

function readJson(key, fallback) {
  if (typeof sessionStorage === 'undefined') return fallback;
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key, value) {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch { /* quota */ }
}

/** @returns {Record<string, unknown> | null} */
export function getLeadSessionProfile() {
  const profile = readJson(PROFILE_KEY, null);
  if (!profile?.applicantEmail || !profile?.consentLeadSharing) return null;
  return profile;
}

/** @param {Record<string, unknown>} profile */
export function saveLeadSessionProfile(profile) {
  if (!profile?.applicantEmail || !profile?.consentLeadSharing) return;
  const existing = readJson(PROFILE_KEY, null) || {};
  writeJson(PROFILE_KEY, {
    ...existing,
    ...profile,
    savedAt: new Date().toISOString(),
  });
}

export function clearLeadSessionProfile() {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.removeItem(PROFILE_KEY);
  sessionStorage.removeItem(SENT_LISTINGS_KEY);
}

export function isListingLeadSentInSession(listingId) {
  if (!listingId) return false;
  const sent = readJson(SENT_LISTINGS_KEY, []);
  return Array.isArray(sent) && sent.includes(listingId);
}

export function markListingLeadSent(listingId) {
  if (!listingId) return;
  const sent = readJson(SENT_LISTINGS_KEY, []);
  const next = Array.isArray(sent) ? [...new Set([...sent, listingId])] : [listingId];
  writeJson(SENT_LISTINGS_KEY, next);
}

export function profileToFormDefaults(profile) {
  if (!profile) {
    return null;
  }
  return {
    applicantName: profile.applicantName || '',
    applicantEmail: profile.applicantEmail || '',
    applicantPhone: profile.applicantPhone || '',
    desiredStartDate: profile.desiredStartDate || '',
    desiredEndDate: profile.desiredEndDate || '',
    stayDurationMonths: profile.stayDurationMonths ?? '',
    budgetMax: profile.budgetMax ?? null,
    occupants: profile.occupants ?? 1,
    stayReason: profile.stayReason || '',
    comments: profile.comments || '',
    consentLeadSharing: true,
  };
}

export async function getLeadAuthHeaders() {
  const headers = { 'Content-Type': 'application/json' };
  try {
    const user = getAuth().currentUser;
    if (user) {
      headers.Authorization = `Bearer ${await user.getIdToken()}`;
    }
  } catch { /* anonymous */ }
  return headers;
}

export function buildLeadPayload(form, { city, searchFilters, abVariant, todayDate }) {
  const startDate = form.desiredStartDate || todayDate;
  const durationMonths = form.stayDurationMonths || 1;
  const computedEndDate = form.desiredEndDate || (() => {
    const d = new Date(startDate);
    d.setMonth(d.getMonth() + Number(durationMonths));
    return d.toISOString().split('T')[0];
  })();

  return {
    applicantName: (form.applicantName || '').trim(),
    applicantEmail: (form.applicantEmail || '').trim().toLowerCase(),
    applicantPhone: form.applicantPhone?.trim() || '',
    desiredStartDate: startDate,
    desiredEndDate: computedEndDate,
    stayDurationMonths: Number(durationMonths),
    budgetMax: form.budgetMax ? Number(form.budgetMax) : null,
    occupants: form.occupants || 1,
    stayReason: form.stayReason || null,
    comments: form.comments?.trim() || null,
    consentLeadSharing: form.consentLeadSharing === true,
    privacyPolicyVersion: PRIVACY_POLICY_VERSION,
    city: city || '',
    searchFilters: searchFilters || null,
    abVariant: abVariant || null,
  };
}

function payloadToSessionProfile(payload) {
  return {
    applicantName: payload.applicantName,
    applicantEmail: payload.applicantEmail,
    applicantPhone: payload.applicantPhone,
    desiredStartDate: payload.desiredStartDate,
    desiredEndDate: payload.desiredEndDate,
    stayDurationMonths: payload.stayDurationMonths,
    budgetMax: payload.budgetMax,
    occupants: payload.occupants,
    stayReason: payload.stayReason,
    comments: payload.comments,
    consentLeadSharing: payload.consentLeadSharing,
    privacyPolicyVersion: payload.privacyPolicyVersion,
    abVariant: payload.abVariant,
  };
}

async function postLead(url, payload) {
  const headers = await getLeadAuthHeaders();
  const res = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, data };
}

/** @returns {{ ok: boolean, status: number, data: any, leadId?: string }} */
export async function submitListingLead(listingId, formOrProfile, { city, searchFilters, abVariant }) {
  const todayDate = new Date().toISOString().split('T')[0];
  const payload = buildLeadPayload(formOrProfile, {
    city,
    searchFilters,
    abVariant: abVariant ?? formOrProfile.abVariant,
    todayDate,
  });
  const result = await postLead(`/api/colivings/scraped-leads/listing/${listingId}`, payload);
  if (result.ok) {
    saveLeadSessionProfile(payloadToSessionProfile(payload));
    markListingLeadSent(listingId);
  }
  return { ...result, leadId: result.data?.leadId };
}

/** @returns {{ ok: boolean, status: number, data: any, leadId?: string }} */
export async function submitSearchLead(form, { city, searchFilters, abVariant }) {
  const todayDate = new Date().toISOString().split('T')[0];
  const payload = buildLeadPayload(form, { city, searchFilters, abVariant, todayDate });
  const result = await postLead('/api/colivings/scraped-leads/search', payload);
  if (result.ok) {
    saveLeadSessionProfile(payloadToSessionProfile(payload));
  }
  return { ...result, leadId: result.data?.leadId };
}

/** @returns {{ ok: boolean, status: number, data: any, leadId?: string }} */
export async function submitExitLead(form, { city, searchFilters }) {
  const todayDate = new Date().toISOString().split('T')[0];
  const nameFromEmail = form.applicantEmail.split('@')[0] || 'Usuario';
  const endDate = (() => {
    const d = new Date(todayDate);
    d.setMonth(d.getMonth() + 1);
    return d.toISOString().split('T')[0];
  })();

  const payload = {
    applicantName: nameFromEmail,
    applicantEmail: form.applicantEmail.trim().toLowerCase(),
    applicantPhone: '',
    desiredStartDate: todayDate,
    desiredEndDate: endDate,
    stayDurationMonths: 1,
    budgetMax: null,
    occupants: 1,
    stayReason: null,
    comments: 'Exit-intent capture',
    consentLeadSharing: form.consentLeadSharing === true,
    privacyPolicyVersion: PRIVACY_POLICY_VERSION,
    city: city || '',
    searchFilters: searchFilters || null,
    abVariant: 'exit_intent',
  };

  const result = await postLead('/api/colivings/scraped-leads/search', payload);
  if (result.ok) {
    saveLeadSessionProfile(payloadToSessionProfile(payload));
  }
  return { ...result, leadId: result.data?.leadId };
}

export function getColivingDisplayTitle(coliving) {
  if (!coliving) return '';
  return coliving.title || coliving.propertyTitle || coliving.externalSource || '';
}
